import { useState } from "react";
import { CanAccess, useRecordContext, useTranslate } from "ra-core";
import { CreateButton } from "@/components/admin/create-button";
import { DataTable } from "@/components/admin/data-table";
import { EditButton } from "@/components/admin/edit-button";
import { ExportButton } from "@/components/admin/export-button";
import { List } from "@/components/admin/list";
import { ReferenceField } from "@/components/admin/reference-field";
import { SearchInput } from "@/components/admin/search-input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import { StatusPill } from "../design/StatusPill";
import { TopToolbar } from "../layout/TopToolbar";
import { isLegacyBusinessArm, type AppRole, type Sale } from "../types";
import { BUSINESS_ARM_LABELS } from "./businessArms";
import { BusinessUnitReviewBadge } from "./BusinessUnitReviewBadge";
import { PendingRosterClaims } from "./PendingRosterClaims";
import { ROLE_LABELS } from "./roles";
import {
  SalesProjectsField,
  SalesProjectsListProvider,
} from "./SalesProjectsField";

// design-system.md section 8: a designed empty value, never the raw
// "null"/"undefined" a missing business unit or clave would otherwise render as.
const EMPTY_VALUE = "—";

const BusinessUnitField = () => {
  const record = useRecordContext<Sale>();
  if (!record) return null;
  return (
    <span className="flex flex-wrap items-center gap-2">
      {record.business_arm
        ? BUSINESS_ARM_LABELS[record.business_arm]
        : EMPTY_VALUE}
      {isLegacyBusinessArm(record.business_arm) ? (
        <BusinessUnitReviewBadge />
      ) : null}
    </span>
  );
};

const AdvisorCodeField = () => {
  const record = useRecordContext<Sale>();
  if (!record) return null;
  return <span>{record.advisor_code || EMPTY_VALUE}</span>;
};

const AccompanimentNameField = () => {
  const record = useRecordContext<Sale>();
  if (!record) return null;
  return (
    <span>
      {record.first_name} {record.last_name}
    </span>
  );
};

const SalesListActions = () => (
  <TopToolbar>
    <ExportButton />
    {/* A lead_manager lists/exports the team but never creates a user
        (Almikia Epic E3b, TASK-063: only direccion does). */}
    <CanAccess resource="sales" action="create">
      <CreateButton label="resources.sales.action.new" variant="gradient" />
    </CanAccess>
  </TopToolbar>
);

const filters = [<SearchInput source="q" alwaysOn />];

const OptionsField = (_props: { label?: string | boolean }) => {
  const record = useRecordContext();
  const translate = useTranslate();
  if (!record) return null;
  return (
    <div className="flex flex-row gap-1">
      {record.role && (
        <Badge
          variant="outline"
          className={record.role === "direccion" ? "border-primary" : undefined}
        >
          {ROLE_LABELS[record.role as AppRole]}
        </Badge>
      )}
      {record.disabled && (
        // design-system.md section 3: a binary state (here, whether the
        // account is disabled) is always a green/red semaforo with icon and
        // text, never color alone -- so this reuses StatusPill instead of a
        // plain Badge.
        <StatusPill
          tone="not-ok"
          text={translate("resources.sales.fields.disabled")}
        />
      )}
    </div>
  );
};

/**
 * Team bajas (owner, 2026-10-19): the team list shows only ACTIVE people by default; people given
 * a baja (disabled accounts) appear only under the "Bajas" filter, so nobody who left looks active.
 * Nothing is deleted: the filter only chooses which side of `disabled` is listed.
 */
const TeamStatusFilter = ({
  showBajas,
  onChange,
}: {
  showBajas: boolean;
  onChange: (showBajas: boolean) => void;
}) => {
  const translate = useTranslate();
  return (
    <div
      role="group"
      aria-label={translate("crm.team_status_filter.label")}
      className="flex gap-2"
    >
      <Button
        type="button"
        variant={showBajas ? "outline" : "default"}
        className="h-11"
        aria-pressed={!showBajas}
        onClick={() => onChange(false)}
      >
        {translate("crm.team_status_filter.active")}
      </Button>
      <Button
        type="button"
        variant={showBajas ? "default" : "outline"}
        className="h-11"
        aria-pressed={showBajas}
        onClick={() => onChange(true)}
      >
        {translate("crm.team_status_filter.departed")}
      </Button>
    </div>
  );
};

export function SalesList() {
  const [showBajas, setShowBajas] = useState(false);
  return (
    <div className="flex flex-col gap-4">
      {/* Almikia Epic E3b-2 (TASK-085): direccion/lead_manager-only section,
          gated by `<PendingRosterClaims>` itself (`CanAccess resource=
          "team_roster"`) -- every other role never renders it. */}
      <PendingRosterClaims />
      {/* Almikia Epic E3b-2 (TASK-086): wraps the whole list so every
          `SalesProjectsField` row shares the SAME two queries instead of
          issuing its own. */}
      <TeamStatusFilter showBajas={showBajas} onChange={setShowBajas} />
      <SalesProjectsListProvider>
        <List
          filter={{ "disabled@eq": showBajas }}
          filters={filters}
          actions={<SalesListActions />}
          sort={{ field: "first_name", order: "ASC" }}
        >
          {/*
           * rowClick is disabled: a lead_manager only edits users within her
           * brazo scope (Almikia Epic E3b, TASK-063), so the edit action below
           * -- gated per row by `canAccess` -- is the only way in, instead of a
           * whole row that looks clickable but silently does nothing for an
           * out-of-scope peer.
           */}
          <DataTable rowClick={false}>
            <DataTable.Col source="first_name" />
            <DataTable.Col source="last_name" />
            <DataTable.Col source="email" />
            <DataTable.Col
              source="business_arm"
              label="resources.sales.fields.business_arm"
            >
              <BusinessUnitField />
            </DataTable.Col>
            <DataTable.Col
              source="advisor_code"
              label="resources.sales.fields.advisor_code"
            >
              <AdvisorCodeField />
            </DataTable.Col>
            <DataTable.Col
              source="reports_to"
              label="resources.sales.fields.reports_to"
            >
              {/* Almikia Epic E3b-2 (TASK-085), Independence-guide wording:
                "acompañamiento", never "reporta a". Populated by
                confirmTeamRosterMatch; a "—" empty value until then. */}
              <ReferenceField
                source="reports_to"
                reference="sales"
                empty={EMPTY_VALUE}
              >
                <AccompanimentNameField />
              </ReferenceField>
            </DataTable.Col>
            {/* Almikia Epic E3b-2 (TASK-086): each person's active projects,
              with a "Líder BPO" marker -- read-only here, everyone who
              reaches the team list sees it (RLS reads `sales_projects` for
              every authenticated role, ADR-ee2f59a9-TASK-079); editing lives
              only on the edit page, gated there to direccion. */}
            <DataTable.Col label="resources.sales.fields.projects">
              <SalesProjectsField />
            </DataTable.Col>
            <DataTable.Col label={false}>
              <OptionsField />
            </DataTable.Col>
            <DataTable.Col label={false}>
              <CanAccess resource="sales" action="edit">
                <EditButton />
              </CanAccess>
            </DataTable.Col>
          </DataTable>
        </List>
      </SalesProjectsListProvider>
    </div>
  );
}
